/**
 * VoiceProfileCard
 *
 * Standalone card showing voice recognition status for a conversation partner.
 * Displayed on the Partner Detail page below the partner header, above the memory card.
 *
 * Responsibilities:
 * - Shows whether a voice sample has been recorded for speaker auto-identification
 * - Displays clear status indicator (recorded vs not recorded)
 * - Provides a "Record voice sample" action button (currently unavailable — no recording
 *   UI exists yet; button is shown as disabled with a "coming soon" tooltip)
 */

import { Mic, CheckCircle, AlertCircle } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";

export interface VoiceProfileCardProps {
  partnerName: string;
  hasVoiceProfile: boolean;
}

export function VoiceProfileCard({
  partnerName,
  hasVoiceProfile,
}: VoiceProfileCardProps) {
  return (
    <Card className="p-6">
      <div className="mb-3 flex items-center gap-2">
        <Mic className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">
          Voice recognition
        </h3>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        Record a short voice sample so we can identify {partnerName} in your
        reflections automatically. Without this, you&apos;ll need to manually
        label which speaker is which after each recording.
      </p>

      {hasVoiceProfile ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-green-700 dark:text-green-400">
              Voice sample recorded
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled
            title="Re-recording coming soon"
            className="text-muted-foreground"
          >
            Re-record
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            <span className="text-amber-700 dark:text-amber-400">
              No voice sample yet
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled
            title="Voice recording coming soon"
          >
            Record voice sample
          </Button>
        </div>
      )}
    </Card>
  );
}
